import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { 
    ChevronLeft, ChevronRight, Globe, Truck, Users, 
    Camera, BarChart3, Shield, Wrench,
    CheckCircle2, Maximize2, X, Play
} from 'lucide-react';

// Screenshot URLs - these are actual app screenshots
const SCREENSHOTS = {
    landing: "https://customer-assets.emergentagent.com/job_fleet-ops-62/artifacts/demo_landing_v1.png",
    dashboard: "https://customer-assets.emergentagent.com/job_fleet-ops-62/artifacts/demo_dashboard_v1.png",
    vehicles: "https://customer-assets.emergentagent.com/job_fleet-ops-62/artifacts/demo_vehicles_v1.png",
    drivers: "https://customer-assets.emergentagent.com/job_fleet-ops-62/artifacts/demo_drivers_v1.png",
    analytics: "https://customer-assets.emergentagent.com/job_fleet-ops-62/artifacts/demo_analytics_v1.png",
    inspections: "https://customer-assets.emergentagent.com/job_fleet-ops-62/artifacts/demo_inspections_v1.png",
    checkin: "https://customer-assets.emergentagent.com/job_fleet-ops-62/artifacts/demo_checkin_v1.png",
    pricing: "https://customer-assets.emergentagent.com/job_fleet-ops-62/artifacts/demo_pricing_v1.png",
};

const slides = [
    {
        id: 'intro',
        titleEn: 'Welcome to Cofleet',
        titleEl: 'Καλώς ήρθατε στο Cofleet',
        subtitleEn: 'Fleet Maintenance Software',
        subtitleEl: 'Λογισμικό Συντήρησης Στόλου',
        descriptionEn: 'Cofleet is a comprehensive SaaS platform designed for fleet operators of all sizes. From individual fleet owners to large enterprises, our solution provides the tools you need to manage your vehicles, drivers, and operations efficiently.',
        descriptionEl: 'Το Cofleet είναι μια ολοκληρωμένη πλατφόρμα SaaS σχεδιασμένη για διαχειριστές στόλου όλων των μεγεθών. Από μεμονωμένους ιδιοκτήτες στόλου έως μεγάλες επιχειρήσεις, η λύση μας παρέχει τα εργαλεία που χρειάζεστε.',
        type: 'hero',
        features: [
            { icon: Truck, labelEn: '4 Pricing Plans', labelEl: '4 Πλάνα Τιμολόγησης' },
            { icon: Users, labelEn: '14-Day Free Trial', labelEl: '14 Ημέρες Δωρεάν Δοκιμή' },
            { icon: Globe, labelEn: 'Bilingual (EN/GR)', labelEl: 'Δίγλωσσο (EN/GR)' }
        ]
    },
    {
        id: 'roles',
        titleEn: 'Role-Based Access Control',
        titleEl: 'Έλεγχος Πρόσβασης με Βάση τον Ρόλο',
        subtitleEn: '3 User Roles with Tailored Permissions',
        subtitleEl: '3 Ρόλοι Χρηστών με Προσαρμοσμένα Δικαιώματα',
        descriptionEn: 'Cofleet implements a comprehensive role-based access system that ensures each user sees only what they need. From Admins managing the company to Drivers performing daily check-ins, every role has its purpose.',
        descriptionEl: 'Το Cofleet υλοποιεί ένα ολοκληρωμένο σύστημα πρόσβασης βασισμένο σε ρόλους. Από τους Διαχειριστές που διαχειρίζονται την εταιρεία έως τους Οδηγούς που κάνουν καθημερινούς ελέγχους.',
        type: 'roles',
        roles: [
            { 
                nameEn: 'Admin', 
                nameEl: 'Διαχειριστής',
                color: 'bg-red-500',
                permissionsEn: ['Full company access', 'User management', 'Analytics & Reports', 'All vehicle operations'],
                permissionsEl: ['Πλήρης πρόσβαση εταιρείας', 'Διαχείριση χρηστών', 'Στατιστικά & Αναφορές', 'Όλες οι λειτουργίες οχημάτων']
            },
            { 
                nameEn: 'Fleet Manager', 
                nameEl: 'Διαχειριστής Στόλου',
                color: 'bg-[#1B7EB5]',
                permissionsEn: ['Vehicle management', 'Driver management', 'Maintenance scheduling', 'View analytics'],
                permissionsEl: ['Διαχείριση οχημάτων', 'Διαχείριση οδηγών', 'Προγραμματισμός συντήρησης', 'Προβολή αναλυτικών']
            },
            { 
                nameEn: 'Driver', 
                nameEl: 'Οδηγός',
                color: 'bg-green-500',
                permissionsEn: ['Personal dashboard', 'Daily check-ins', 'Report damages', 'View assigned vehicles'],
                permissionsEl: ['Προσωπικός πίνακας', 'Καθημερινοί έλεγχοι', 'Αναφορά ζημιών', 'Προβολή οχημάτων']
            }
        ]
    },
    {
        id: 'dashboard',
        titleEn: 'Admin Dashboard',
        titleEl: 'Πίνακας Διαχειριστή',
        subtitleEn: 'Real-Time Fleet Overview',
        subtitleEl: 'Επισκόπηση Στόλου σε Πραγματικό Χρόνο',
        descriptionEn: 'The admin dashboard provides instant visibility into your fleet operations. Monitor vehicle status, track maintenance schedules, analyze fuel costs, and view key performance indicators all in one place.',
        descriptionEl: 'Ο πίνακας διαχειριστή παρέχει άμεση ορατότητα στις λειτουργίες του στόλου σας. Παρακολουθήστε την κατάσταση οχημάτων, προγράμματα συντήρησης, κόστη καυσίμων.',
        type: 'screenshot',
        screenshot: 'dashboard',
        icon: BarChart3,
        highlights: [
            { labelEn: 'Vehicle Tracking', labelEl: 'Παρακολούθηση Οχημάτων', value: 'Real-time' },
            { labelEn: 'Fuel Analytics', labelEl: 'Στατιστικά Καυσίμων', value: '6 months' },
            { labelEn: 'Maintenance Alerts', labelEl: 'Ειδοποιήσεις Συντήρησης', value: 'Auto' }
        ]
    },
    {
        id: 'vehicles',
        titleEn: 'Vehicle Management',
        titleEl: 'Διαχείριση Οχημάτων',
        subtitleEn: 'Complete Vehicle Lifecycle Tracking',
        subtitleEl: 'Πλήρης Παρακολούθηση Κύκλου Ζωής Οχημάτων',
        descriptionEn: 'Manage your entire fleet from a single interface. Track vehicle details, monitor mileage, assign drivers, and maintain comprehensive records. Each vehicle card shows status, current assignment, and maintenance needs.',
        descriptionEl: 'Διαχειριστείτε ολόκληρο τον στόλο σας από μία διεπαφή. Παρακολουθήστε λεπτομέρειες οχημάτων, χιλιόμετρα, αναθέσεις οδηγών και διατηρήστε ολοκληρωμένα αρχεία.',
        type: 'screenshot',
        screenshot: 'vehicles',
        icon: Truck,
        highlights: [
            { labelEn: 'Vehicle Status', labelEl: 'Κατάσταση Οχήματος', value: 'Active/Maintenance' },
            { labelEn: 'Mileage Tracking', labelEl: 'Παρακολούθηση Χιλιομέτρων', value: 'Automatic' },
            { labelEn: 'Driver Assignment', labelEl: 'Ανάθεση Οδηγού', value: 'Flexible' }
        ]
    },
    {
        id: 'drivers',
        titleEn: 'Driver Management',
        titleEl: 'Διαχείριση Οδηγών',
        subtitleEn: 'Comprehensive Driver Profiles',
        subtitleEl: 'Ολοκληρωμένα Προφίλ Οδηγών',
        descriptionEn: 'Keep track of all your drivers with detailed profiles including contact information, license details, expiration dates, and availability status. Quickly identify drivers who are available, on trip, or need license renewal.',
        descriptionEl: 'Παρακολουθήστε όλους τους οδηγούς σας με λεπτομερή προφίλ που περιλαμβάνουν στοιχεία επικοινωνίας, λεπτομέρειες άδειας, ημερομηνίες λήξης και κατάσταση διαθεσιμότητας.',
        type: 'screenshot',
        screenshot: 'drivers',
        icon: Users,
        highlights: [
            { labelEn: 'License Tracking', labelEl: 'Παρακολούθηση Αδειών', value: 'Expiry alerts' },
            { labelEn: 'Availability', labelEl: 'Διαθεσιμότητα', value: 'Real-time' },
            { labelEn: 'Contact Info', labelEl: 'Στοιχεία Επικοινωνίας', value: 'Complete' }
        ]
    },
    {
        id: 'checkin',
        titleEn: 'Driver Daily Check-In',
        titleEl: 'Καθημερινός Έλεγχος Οδηγού',
        subtitleEn: 'Mobile-Optimized Inspection Flow',
        subtitleEl: 'Ροή Επιθεώρησης για Κινητά',
        descriptionEn: 'Drivers can perform daily vehicle inspections directly from their mobile devices. The intuitive step-by-step process guides them through vehicle selection, odometer recording, and capturing 6 photos from different angles.',
        descriptionEl: 'Οι οδηγοί μπορούν να πραγματοποιήσουν καθημερινές επιθεωρήσεις οχημάτων απευθείας από τις κινητές τους συσκευές. Η διαδικασία βήμα-βήμα τους καθοδηγεί.',
        type: 'screenshot',
        screenshot: 'checkin',
        icon: Camera,
        highlights: [
            { labelEn: 'Photo Capture', labelEl: 'Λήψη Φωτογραφιών', value: '6 angles' },
            { labelEn: 'Odometer Entry', labelEl: 'Καταχώρηση Χιλιομέτρων', value: 'Required' },
            { labelEn: 'Mobile Ready', labelEl: 'Έτοιμο για Κινητά', value: 'Optimized' }
        ]
    },
    {
        id: 'analytics',
        titleEn: 'Fleet Analytics',
        titleEl: 'Στατιστικά Στόλου',
        subtitleEn: 'Data-Driven Decision Making',
        subtitleEl: 'Αποφάσεις με Βάση τα Δεδομένα',
        descriptionEn: 'Comprehensive analytics provide insights into vehicle utilization, fuel consumption, maintenance costs, and driver performance. Export reports as CSV for further analysis or integrate with your existing tools.',
        descriptionEl: 'Τα ολοκληρωμένα αναλυτικά παρέχουν πληροφορίες για τη χρήση οχημάτων, την κατανάλωση καυσίμων, τα κόστη συντήρησης και την απόδοση οδηγών.',
        type: 'screenshot',
        screenshot: 'analytics',
        icon: BarChart3,
        highlights: [
            { labelEn: 'KM Predictions', labelEl: 'Προβλέψεις ΧΛΜ', value: 'Year-end' },
            { labelEn: 'Cost Analysis', labelEl: 'Ανάλυση Κόστους', value: 'Per vehicle' },
            { labelEn: 'CSV Export', labelEl: 'Εξαγωγή CSV', value: 'One-click' }
        ]
    },
    {
        id: 'inspections',
        titleEn: 'Photo Inspections',
        titleEl: 'Επιθεωρήσεις με Φωτογραφίες',
        subtitleEn: 'AI-Powered Damage Detection',
        subtitleEl: 'Ανίχνευση Ζημιών με Τεχνητή Νοημοσύνη',
        descriptionEn: 'All daily inspections are logged with photos and odometer readings. Our GPT-4o Vision AI automatically analyzes photos to detect damages, scratches, or issues that might be missed by visual inspection alone.',
        descriptionEl: 'Όλες οι καθημερινές επιθεωρήσεις καταγράφονται με φωτογραφίες και ενδείξεις χιλιομέτρων. Η τεχνητή νοημοσύνη GPT-4o Vision αναλύει αυτόματα τις φωτογραφίες.',
        type: 'screenshot',
        screenshot: 'inspections',
        icon: Camera,
        highlights: [
            { labelEn: 'AI Analysis', labelEl: 'Ανάλυση AI', value: 'GPT-4o Vision' },
            { labelEn: 'Photo Compare', labelEl: 'Σύγκριση Φωτογραφιών', value: 'Side-by-side' },
            { labelEn: 'Status Tracking', labelEl: 'Παρακολούθηση Κατάστασης', value: 'Approved/Flagged' }
        ]
    },
    {
        id: 'bilingual',
        titleEn: 'Bilingual Interface',
        titleEl: 'Δίγλωσση Διεπαφή',
        subtitleEn: 'Full English & Greek Support',
        subtitleEl: 'Πλήρης Υποστήριξη Αγγλικών & Ελληνικών',
        descriptionEn: 'Cofleet is fully bilingual with complete English and Greek translations. Users can switch languages with a single click, and their preference is remembered. All UI text, labels, and messages are properly localized.',
        descriptionEl: 'Το Cofleet είναι πλήρως δίγλωσσο με ολοκληρωμένες μεταφράσεις στα Αγγλικά και τα Ελληνικά. Οι χρήστες μπορούν να αλλάξουν γλώσσα με ένα κλικ.',
        type: 'bilingual',
        examples: [
            { en: 'Dashboard', el: 'Πίνακας Ελέγχου' },
            { en: 'Vehicles', el: 'Οχήματα' },
            { en: 'Drivers', el: 'Οδηγοί' },
            { en: 'Maintenance', el: 'Συντήρηση' },
            { en: 'Analytics', el: 'Στατιστικά' },
            { en: 'Settings', el: 'Ρυθμίσεις' }
        ]
    },
    {
        id: 'pricing',
        titleEn: 'Flexible Pricing Plans',
        titleEl: 'Ευέλικτα Πλάνα Τιμολόγησης',
        subtitleEn: 'Pay Per Vehicle',
        subtitleEl: 'Πληρωμή ανά Όχημα',
        descriptionEn: 'Simple, transparent pricing based on your fleet size. All plans include unlimited drivers and full features. Start with a 14-day free trial - no credit card required.',
        descriptionEl: 'Απλή, διαφανής τιμολόγηση βάσει του μεγέθους του στόλου σας. Όλα τα πλάνα περιλαμβάνουν απεριόριστους οδηγούς και πλήρεις λειτουργίες. Ξεκινήστε με 14 ημέρες δωρεάν δοκιμή.',
        type: 'pricing',
        plans: [
            { nameEn: 'Basic', nameEl: 'Βασικό', price: '€12', vehiclesEn: 'Up to 7 vehicles', vehiclesEl: 'Έως 7 οχήματα', pricePerEn: '/vehicle/mo', pricePerEl: '/όχημα/μήνα', popular: true },
            { nameEn: 'Professional', nameEl: 'Επαγγελματικό', price: '€10', vehiclesEn: 'Up to 15 vehicles', vehiclesEl: 'Έως 15 οχήματα', pricePerEn: '/vehicle/mo', pricePerEl: '/όχημα/μήνα' },
            { nameEn: 'Enterprise', nameEl: 'Εταιρικό', price: '€8', vehiclesEn: 'Up to 30 vehicles', vehiclesEl: 'Έως 30 οχήματα', pricePerEn: '/vehicle/mo', pricePerEl: '/όχημα/μήνα' },
            { nameEn: 'Custom', nameEl: 'Προσαρμοσμένο', price: 'Custom', vehiclesEn: 'Unlimited', vehiclesEl: 'Απεριόριστα' }
        ]
    },
    {
        id: 'cta',
        titleEn: 'Ready to Get Started?',
        titleEl: 'Έτοιμοι να Ξεκινήσετε;',
        subtitleEn: 'Start Your 14-Day Free Trial Today',
        subtitleEl: 'Ξεκινήστε τη Δωρεάν Δοκιμή 14 Ημερών',
        descriptionEn: 'Join hundreds of fleet operators who trust Cofleet to manage their vehicles efficiently. No credit card required, full access to all features during your trial period.',
        descriptionEl: 'Γίνετε μέλος εκατοντάδων διαχειριστών στόλου που εμπιστεύονται το Cofleet για την αποτελεσματική διαχείριση των οχημάτων τους.',
        type: 'cta'
    }
];

export default function DemoPage() {
    const navigate = useNavigate();
    const { language, toggleLanguage } = useLanguage();
    const [currentSlide, setCurrentSlide] = useState(0);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [imageError, setImageError] = useState({});

    const slide = slides[currentSlide];
    const isEn = language === 'en';

    const nextSlide = () => {
        if (currentSlide < slides.length - 1) {
            setCurrentSlide(currentSlide + 1);
        }
    };

    const prevSlide = () => {
        if (currentSlide > 0) {
            setCurrentSlide(currentSlide - 1);
        }
    };

    const goToSlide = (index) => {
        setCurrentSlide(index);
    };

    const handleImageError = (screenshotId) => {
        setImageError(prev => ({ ...prev, [screenshotId]: true }));
    };

    const renderSlideContent = () => {
        switch (slide.type) {
            case 'hero':
                return (
                    <div className="flex flex-col items-center justify-center h-full text-center">
                        <div className="mb-8">
                            <img 
                                src="https://customer-assets-agu9un31.emergentagent.net/job_fleet-ops-62/artifacts/sxi4lya9_%CE%91%CE%BD%CF%89%CC%81%CE%BD%CF%85%CE%BC%CE%BF%20%CF%83%CF%87%CE%B5%CC%81%CE%B4%CE%B9%CE%BF.webp" 
                                alt="Cofleet Logo" 
                                className="w-32 h-32 rounded-2xl mx-auto shadow-lg object-contain bg-white p-2"
                            />
                        </div>
                        <h1 className="text-4xl md:text-5xl font-bold text-slate-800 mb-4">
                            {isEn ? slide.titleEn : slide.titleEl}
                        </h1>
                        <p className="text-xl text-[#1B7EB5] font-medium mb-6">
                            {isEn ? slide.subtitleEn : slide.subtitleEl}
                        </p>
                        <p className="text-lg text-slate-600 max-w-2xl mb-8">
                            {isEn ? slide.descriptionEn : slide.descriptionEl}
                        </p>
                        <div className="flex flex-wrap justify-center gap-6">
                            {slide.features.map((feature, idx) => (
                                <div key={idx} className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm border border-slate-200">
                                    <feature.icon className="w-5 h-5 text-[#1B7EB5]" />
                                    <span className="text-slate-700 font-medium">{isEn ? feature.labelEn : feature.labelEl}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                );

            case 'roles':
                return (
                    <div className="h-full">
                        <div className="text-center mb-8">
                            <h2 className="text-3xl font-bold text-slate-800 mb-2">
                                {isEn ? slide.titleEn : slide.titleEl}
                            </h2>
                            <p className="text-lg text-slate-600">
                                {isEn ? slide.subtitleEn : slide.subtitleEl}
                            </p>
                        </div>
                        <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
                            {slide.roles.map((role, idx) => (
                                <div key={idx} className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
                                    <div className={`w-12 h-12 ${role.color} rounded-lg flex items-center justify-center mb-4`}>
                                        <Shield className="w-6 h-6 text-white" />
                                    </div>
                                    <h3 className="font-bold text-slate-800 text-lg mb-3">
                                        {isEn ? role.nameEn : role.nameEl}
                                    </h3>
                                    <ul className="space-y-2">
                                        {(isEn ? role.permissionsEn : role.permissionsEl).map((perm, pIdx) => (
                                            <li key={pIdx} className="flex items-start gap-2 text-sm text-slate-600">
                                                <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                                                <span>{perm}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </div>
                    </div>
                );

            case 'screenshot':
                const Icon = slide.icon;
                const screenshotUrl = SCREENSHOTS[slide.screenshot];
                const hasError = imageError[slide.screenshot];
                
                return (
                    <div className="h-full">
                        <div className="text-center mb-6">
                            <h2 className="text-3xl font-bold text-slate-800 mb-2">
                                {isEn ? slide.titleEn : slide.titleEl}
                            </h2>
                            <p className="text-slate-600">
                                {isEn ? slide.subtitleEn : slide.subtitleEl}
                            </p>
                        </div>
                        <div className="grid lg:grid-cols-3 gap-6">
                            <div className="lg:col-span-2 bg-white rounded-xl shadow-lg overflow-hidden border border-slate-200">
                                <div className="bg-slate-100 px-4 py-2 flex items-center gap-2 border-b">
                                    <div className="flex gap-1.5">
                                        <div className="w-3 h-3 rounded-full bg-red-400"></div>
                                        <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                                        <div className="w-3 h-3 rounded-full bg-green-400"></div>
                                    </div>
                                    <span className="text-xs text-slate-500 ml-2">Cofleet App</span>
                                </div>
                                <div className="aspect-video bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center relative overflow-hidden">
                                    {!hasError && screenshotUrl ? (
                                        <img 
                                            src={screenshotUrl} 
                                            alt={isEn ? slide.titleEn : slide.titleEl}
                                            className="w-full h-full object-cover"
                                            onError={() => handleImageError(slide.screenshot)}
                                        />
                                    ) : (
                                        <div className="text-center p-8">
                                            <div className="w-20 h-20 bg-[#1B7EB5]/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                                                <Icon className="w-10 h-10 text-[#1B7EB5]" />
                                            </div>
                                            <h3 className="text-xl font-semibold text-slate-700 mb-2">
                                                {isEn ? slide.titleEn : slide.titleEl}
                                            </h3>
                                            <p className="text-sm text-slate-500 mb-4">
                                                {isEn ? 'Live preview available' : 'Διαθέσιμη ζωντανή προεπισκόπηση'}
                                            </p>
                                            <Button
                                                onClick={() => window.open('https://fleet-ops-62.preview.emergentagent.com', '_blank')}
                                                className="bg-[#1B7EB5] hover:bg-[#1B7EB5]/90 text-white gap-2"
                                            >
                                                <Play className="w-4 h-4" />
                                                {isEn ? 'View Live Demo' : 'Δείτε Ζωντανή Demo'}
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="space-y-4">
                                <p className="text-slate-600 text-sm">
                                    {isEn ? slide.descriptionEn : slide.descriptionEl}
                                </p>
                                <div className="space-y-3">
                                    {slide.highlights.map((highlight, idx) => (
                                        <div key={idx} className="bg-white rounded-lg p-3 border border-slate-200">
                                            <p className="text-xs text-slate-500 uppercase tracking-wide">
                                                {isEn ? highlight.labelEn : highlight.labelEl}
                                            </p>
                                            <p className="text-lg font-semibold text-[#1B7EB5]">{highlight.value}</p>
                                        </div>
                                    ))}
                                </div>
                                <Button
                                    onClick={() => window.open('https://fleet-ops-62.preview.emergentagent.com', '_blank')}
                                    variant="outline"
                                    className="w-full gap-2"
                                >
                                    <Play className="w-4 h-4" />
                                    {isEn ? 'Try Live Demo' : 'Δοκιμάστε τη Demo'}
                                </Button>
                            </div>
                        </div>
                    </div>
                );

            case 'bilingual':
                return (
                    <div className="h-full">
                        <div className="text-center mb-8">
                            <h2 className="text-3xl font-bold text-slate-800 mb-2">
                                {isEn ? slide.titleEn : slide.titleEl}
                            </h2>
                            <p className="text-slate-600">
                                {isEn ? slide.descriptionEn : slide.descriptionEl}
                            </p>
                        </div>
                        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                            <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
                                <div className="flex items-center gap-2 mb-4">
                                    <span className="text-2xl">🇬🇧</span>
                                    <span className="font-bold text-slate-800">English</span>
                                </div>
                                <div className="space-y-2">
                                    {slide.examples.map((ex, idx) => (
                                        <div key={idx} className="px-3 py-2 bg-slate-50 rounded-lg text-slate-700">
                                            {ex.en}
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
                                <div className="flex items-center gap-2 mb-4">
                                    <span className="text-2xl">🇬🇷</span>
                                    <span className="font-bold text-slate-800">Ελληνικά</span>
                                </div>
                                <div className="space-y-2">
                                    {slide.examples.map((ex, idx) => (
                                        <div key={idx} className="px-3 py-2 bg-slate-50 rounded-lg text-slate-700">
                                            {ex.el}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                );

            case 'pricing':
                return (
                    <div className="h-full">
                        <div className="text-center mb-8">
                            <h2 className="text-3xl font-bold text-slate-800 mb-2">
                                {isEn ? slide.titleEn : slide.titleEl}
                            </h2>
                            <p className="text-slate-600">
                                {isEn ? slide.subtitleEn : slide.subtitleEl}
                            </p>
                        </div>
                        <div className="grid md:grid-cols-4 gap-4 max-w-5xl mx-auto">
                            {slide.plans.map((plan, idx) => (
                                <div 
                                    key={idx} 
                                    className={`bg-white rounded-xl p-5 shadow-sm border ${plan.popular ? 'border-[#1B7EB5] ring-2 ring-[#1B7EB5]/20' : 'border-slate-200'}`}
                                >
                                    {plan.popular && (
                                        <span className="inline-block px-2 py-1 bg-[#1B7EB5] text-white text-xs font-medium rounded-full mb-3">
                                            {isEn ? 'Most Popular' : 'Πιο Δημοφιλές'}
                                        </span>
                                    )}
                                    <h3 className="font-bold text-slate-800 text-lg">
                                        {isEn ? plan.nameEn : plan.nameEl}
                                    </h3>
                                    <p className="text-3xl font-bold text-[#1B7EB5] my-2">
                                        {plan.price}
                                        {plan.pricePerEn && <span className="text-sm font-normal text-slate-500">{isEn ? plan.pricePerEn : plan.pricePerEl}</span>}
                                    </p>
                                    <p className="text-sm text-slate-600">
                                        {isEn ? plan.vehiclesEn : plan.vehiclesEl}
                                    </p>
                                    <p className="text-xs text-[#8CC774] mt-1">
                                        {isEn ? 'Unlimited drivers' : 'Απεριόριστοι οδηγοί'}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                );

            case 'cta':
                return (
                    <div className="flex flex-col items-center justify-center h-full text-center">
                        <div className="w-20 h-20 bg-gradient-to-br from-[#1B7EB5] to-[#8CC774] rounded-2xl flex items-center justify-center mb-6 shadow-lg">
                            <Truck className="w-10 h-10 text-white" />
                        </div>
                        <h2 className="text-4xl font-bold text-slate-800 mb-4">
                            {isEn ? slide.titleEn : slide.titleEl}
                        </h2>
                        <p className="text-xl text-[#1B7EB5] font-medium mb-4">
                            {isEn ? slide.subtitleEn : slide.subtitleEl}
                        </p>
                        <p className="text-lg text-slate-600 max-w-xl mb-8">
                            {isEn ? slide.descriptionEn : slide.descriptionEl}
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4">
                            <Button 
                                onClick={() => navigate('/register')}
                                className="bg-[#1B7EB5] hover:bg-[#1B7EB5]/90 text-white h-12 px-8 text-lg"
                            >
                                {isEn ? 'Start Free Trial' : 'Ξεκινήστε Δωρεάν Δοκιμή'}
                            </Button>
                            <Button 
                                variant="outline"
                                onClick={() => navigate('/login')}
                                className="h-12 px-8 text-lg"
                            >
                                {isEn ? 'Sign In' : 'Σύνδεση'}
                            </Button>
                        </div>
                    </div>
                );

            default:
                return null;
        }
    };

    return (
        <div className={`min-h-screen bg-gradient-to-br from-slate-50 via-white to-[#1B7EB5]/5 ${isFullscreen ? 'fixed inset-0 z-50' : ''}`}>
            {/* Header */}
            <header className="fixed top-0 left-0 right-0 bg-white/90 backdrop-blur-sm border-b border-slate-200 z-40">
                <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <img 
                            src="https://customer-assets-agu9un31.emergentagent.net/job_fleet-ops-62/artifacts/sxi4lya9_%CE%91%CE%BD%CF%89%CC%81%CE%BD%CF%85%CE%BC%CE%BF%20%CF%83%CF%87%CE%B5%CC%81%CE%B4%CE%B9%CE%BF.webp" 
                            alt="Cofleet" 
                            className="w-10 h-10 rounded-lg object-contain"
                        />
                        <span className="font-bold text-slate-800">Cofleet Demo</span>
                        <span className="text-sm text-slate-500 hidden sm:inline">| {isEn ? 'Product Presentation' : 'Παρουσίαση Προϊόντος'}</span>
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            onClick={toggleLanguage}
                            className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-slate-200 hover:border-[#1B7EB5] text-slate-600 hover:text-[#1B7EB5] transition-colors"
                            data-testid="demo-language-toggle"
                        >
                            <Globe className="w-4 h-4" />
                            <span className="text-sm font-medium">{language === 'en' ? 'EN' : 'ΕΛ'}</span>
                        </button>
                        <button
                            onClick={() => setIsFullscreen(!isFullscreen)}
                            className="p-2 rounded-lg hover:bg-slate-100 text-slate-600"
                        >
                            {isFullscreen ? <X className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
                        </button>
                        <Button
                            variant="outline"
                            onClick={() => navigate('/')}
                            className="hidden sm:inline-flex"
                        >
                            {isEn ? 'Back to Site' : 'Επιστροφή'}
                        </Button>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="pt-24 pb-32 px-4">
                <div className="max-w-6xl mx-auto">
                    {renderSlideContent()}
                </div>
            </main>

            {/* Navigation Footer */}
            <footer className="fixed bottom-0 left-0 right-0 bg-white/90 backdrop-blur-sm border-t border-slate-200 z-40">
                <div className="max-w-7xl mx-auto px-4 py-4">
                    {/* Progress dots */}
                    <div className="flex justify-center gap-2 mb-4">
                        {slides.map((_, idx) => (
                            <button
                                key={idx}
                                onClick={() => goToSlide(idx)}
                                className={`w-2 h-2 rounded-full transition-all ${
                                    idx === currentSlide 
                                        ? 'w-6 bg-[#1B7EB5]' 
                                        : 'bg-slate-300 hover:bg-slate-400'
                                }`}
                                data-testid={`slide-dot-${idx}`}
                            />
                        ))}
                    </div>
                    
                    {/* Navigation buttons */}
                    <div className="flex items-center justify-between">
                        <Button
                            variant="outline"
                            onClick={prevSlide}
                            disabled={currentSlide === 0}
                            className="gap-2"
                            data-testid="prev-slide-btn"
                        >
                            <ChevronLeft className="w-4 h-4" />
                            {isEn ? 'Previous' : 'Προηγούμενο'}
                        </Button>
                        
                        <span className="text-sm text-slate-500">
                            {currentSlide + 1} / {slides.length}
                        </span>
                        
                        <Button
                            onClick={nextSlide}
                            disabled={currentSlide === slides.length - 1}
                            className="gap-2 bg-[#1B7EB5] hover:bg-[#1B7EB5]/90"
                            data-testid="next-slide-btn"
                        >
                            {isEn ? 'Next' : 'Επόμενο'}
                            <ChevronRight className="w-4 h-4" />
                        </Button>
                    </div>
                </div>
            </footer>
        </div>
    );
}
